<script lang="ts">
  import GroupDirectorSideMenuLinks from "@shared/svelte/Components/Layout/Header/SideMenu/GroupDirectorSideMenuLinks.svelte";
  import SideMenuLink from "@shared/svelte/Components/Layout/Header/SideMenu/SideMenuLink.svelte";
  import MobileGlobalNav from "@shared/svelte/Components/Layout/Header/SideMenu/MobileGlobalNav.svelte";
  import {
    Avatar,
    Button,
    Divider,
    Flex,
    Icon,
    Link,
    MenuLink,
    SideMenu,
    Text,
    BarChart,
    BoxArrowRight,
    CalendarSolid,
    ChatBubbleSolid,
    ChatBubbleStarSolid,
    ChevronRight,
    ClipboardCheckSolid,
    DeliveryTruckSolid,
    DollarSignCircleSolid,
    GearSolid,
    GiftCard,
    LifePreserver,
    ListSolid,
    MegaphoneSolid,
  } from "@getprovi/craft";

  import { EventTracking } from "@shared/services/event_tracking/events";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import { SideMenuLinkEntity } from "@shared/services/event_tracking/entities";

  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
  import CurrentRetailerStore from "@retailer/svelte/stores/CurrentRetailerStore";

  import type { RetailerUser } from "@shared/types/User";
  import type { SideMenuLink as SideMenuLinkType } from "@shared/types/SideMenuLink";

  const { trackClick } = EventTracking;

  const user: RetailerUser = <RetailerUser>$CurrentUserStore;

  let hidden: boolean = true;

  const closeMenu = (): void => {
    hidden = true;
  };

  const handleCloseTracking = (): void => {
    if (!hidden) {
      trackClick("close_side_menu");
    }
  };

  const logout = async (): Promise<void> => {
    trackClick("side_menu_link", [new SideMenuLinkEntity({ label: "Log Out" })]);

    if (user.role == "supplier") {
      await HttpPromise({ method: "POST", url: "/api/retailer/retailers/exit_supplier_preview" });
      window.location.reload();
    } else {
      window.location.assign("/users/fully_log_out");
    }
  };

  const links: SideMenuLinkType[] = [
    {
      href: "/orders",
      iconName: BarChart,
      title: "Order History",
      subtitle: "View & track orders from distributors",
    },
    {
      href: "/lists",
      iconName: ListSolid,
      title: "Lists",
      subtitle: "Order guides",
    },
    {
      href: "/invoices",
      iconName: DollarSignCircleSolid,
      title: "ProviPay Payments",
      subtitle: "Manage and pay distributor invoices",
      shouldShow: $CurrentRetailerStore?.term_payments,
      authName: "invoices.index",
    },
    {
      href: "/settings/accounts",
      iconName: DeliveryTruckSolid,
      title: "My Distributors",
      subtitle: "Your distributors and sales reps",
    },
    {
      href: `https://buyer.sevenfifty.com/events?zipcode=${$CurrentRetailerStore?.zip}`,
      iconName: CalendarSolid,
      title: "Events",
      subtitle: "Explore what's happening in your area",
      external: true,
    },
    {
      href: "/messaging",
      iconName: ChatBubbleSolid,
      title: "Provi Chat",
      subtitle: "Send direct messages to sales reps",
    },
    {
      href: "/rep_recommendations",
      iconName: ChatBubbleStarSolid,
      title: "Rep Recommendations",
      subtitle: "Product sheets from your sales reps",
    },
    {
      href: "/sculpture_reports",
      iconName: ClipboardCheckSolid,
      title: "Sculpture Reports",
      subtitle: "Latest Sculpture inventory counts",
      shouldShow: $CurrentRetailerStore?.has_sculpture,
    },
    {
      href: "/reports",
      iconName: BarChart,
      title: "Reports",
      subtitle: "View stats of your orders",
    },
    {
      href: "/refer",
      iconName: GiftCard,
      title: "Invite a friend - get $100",
    },
  ];

  const bottomLinks: SideMenuLinkType[] = [
    {
      href: "/sc/whatsnew",
      iconName: MegaphoneSolid,
      title: "What's New",
    },
    {
      href: "/settings/profile",
      iconName: GearSolid,
      title: "Account Settings",
    },
    {
      external: true,
      href: "https://support.provi.com",
      iconName: LifePreserver,
      title: "Help",
    },
  ];
</script>

<SideMenu
  bind:hidden
  class="craft"
  clickOutsideCallback={handleCloseTracking}
  closeButtonClick={handleCloseTracking}
  openDrawerClick={() => {
    trackClick("open_side_menu");
  }}
>
  <Flex gap="20" class={user.admin ? "relative mt-4 z-0" : ""} column>
    {#if user.admin}
      <Button
        label="Go to Provi admin"
        size="sm"
        on:click={() => {
          window.location.href = "/admin";
          closeMenu();
        }}
      />
    {/if}
    <Flex gap="12">
      <Avatar label={user.first_name.slice(0, 1)} />
      <div class="grid">
        <Text size="sm" semibold>Hi, {user.first_name}</Text>

        <Link size="sm" href="/settings/profile"
          >View your profile <Icon
            iconName={ChevronRight}
            size="icon-sm"
            color="currentColor"
          /></Link
        >
      </div>
    </Flex>
  </Flex>
  <Divider />
  <MobileGlobalNav {closeMenu} />
  <Divider class="block tablet:hidden" />
  <Flex gap="8" column as="ul">
    <GroupDirectorSideMenuLinks {closeMenu} />

    {#each links as link}
      <SideMenuLink {link} {closeMenu} />
    {/each}
  </Flex>
  <Divider />
  <Flex as="ul" gap="8" column>
    {#each bottomLinks as link}
      <SideMenuLink {link} {closeMenu} />
    {/each}
  </Flex>
  <Divider />
  <MenuLink href="#" title="Log out" on:click={logout}>
    <Icon iconName={BoxArrowRight} color="brand" size="icon-sm" slot="icon" />
  </MenuLink>
</SideMenu>
